/**
 * Trade Parser for Meteora DBC Transactions
 *
 * Parses raw transaction notifications from WebSocket (via Triton Whirligig)
 * into structured TradeEvent objects. Uses pre/post token balances
 * to determine trade direction and amounts.
 */

import type {
  TradeEvent,
  TokenInfo,
  TransactionNotificationResult,
  TokenBalanceEntry,
} from './types';
import { WSOL_MINT } from './types';

const DEBUG = import.meta.env.DEV;

/**
 * Parse a transactionNotification result into a TradeEvent.
 * Returns null if the transaction is not a valid swap or cannot be parsed.
 */
export function parseTransactionNotification(
  result: TransactionNotificationResult,
  tokenInfoCache: Map<string, TokenInfo>,
): TradeEvent | null {
  try {
    const { signature, transaction, meta, blockTime } = result;

    // Skip failed transactions
    if (meta?.err) return null;

    const accountKeys = transaction?.message?.accountKeys || [];
    const preTokenBalances = meta?.preTokenBalances || [];
    const postTokenBalances = meta?.postTokenBalances || [];
    const preBalances = meta?.preBalances || [];
    const postBalances = meta?.postBalances || [];

    // Fee payer is the first account
    const feePayer = accountKeys[0]?.pubkey || '';
    if (!feePayer) return null;

    // Calculate non-WSOL token balance changes per owner
    const ownerChanges = computeTokenChanges(preTokenBalances, postTokenBalances);

    if (ownerChanges.size === 0) return null;

    // Find the fee payer's token change (trader's perspective)
    let traderMint = '';
    let traderChange = 0;

    for (const [key, val] of ownerChanges) {
      if (val.owner === feePayer && Math.abs(val.change) > 0) {
        traderMint = val.mint;
        traderChange = val.change;
        break;
      }
    }

    // Fallback: find the largest non-pool token change
    if (!traderMint) {
      let maxChange = 0;
      for (const [, val] of ownerChanges) {
        if (Math.abs(val.change) > maxChange) {
          maxChange = Math.abs(val.change);
          traderMint = val.mint;
          traderChange = val.change;
        }
      }
    }

    if (!traderMint) return null;

    // Calculate SOL amount from lamport balance changes (fee payer = index 0)
    const preSol = (preBalances[0] || 0) / 1e9;
    const postSol = (postBalances[0] || 0) / 1e9;
    const txFee = (meta?.fee || 5000) / 1e9;
    const amountSol = Math.abs(postSol - preSol + txFee);

    const type: 'BUY' | 'SELL' = traderChange > 0 ? 'BUY' : 'SELL';
    const amountTokens = Math.abs(traderChange);

    // Look up token metadata from cache
    const tokenInfo = tokenInfoCache.get(traderMint);

    const timestamp = blockTime
      ? new Date(blockTime * 1000).toISOString()
      : new Date().toISOString();

    if (DEBUG) {
      console.log(`[tradeParser] Parsed ${type}: ${amountTokens} tokens, ${amountSol.toFixed(4)} SOL, mint=${traderMint.slice(0, 8)}...`);
    }

    return {
      txSignature: signature,
      type,
      trader: feePayer,
      amountSol,
      amountTokens,
      timestamp,
      tokenMint: traderMint,
      tokenName: tokenInfo?.name || '',
      tokenSymbol: tokenInfo?.symbol || '',
      tokenImage: tokenInfo?.imageUrl || null,
    };
  } catch (err) {
    if (DEBUG) {
      console.error('[tradeParser] Parse error:', err);
    }
    return null;
  }
}

interface TokenChange {
  mint: string;
  owner: string;
  change: number;
}

/**
 * Compute net token balance changes per owner from pre/post balances.
 * Skips WSOL entries since we use lamport balances for SOL amounts.
 */
function computeTokenChanges(
  preTokenBalances: TokenBalanceEntry[],
  postTokenBalances: TokenBalanceEntry[],
): Map<string, TokenChange> {
  const changes = new Map<string, TokenChange>();

  // Process post-balances (accounts that exist after the tx)
  for (const post of postTokenBalances) {
    if (post.mint === WSOL_MINT) continue;

    const preEntry = preTokenBalances.find(
      (pre) => pre.accountIndex === post.accountIndex,
    );

    const preAmount = preEntry
      ? parseFloat(preEntry.uiTokenAmount?.uiAmountString || '0')
      : 0;
    const postAmount = parseFloat(post.uiTokenAmount?.uiAmountString || '0');
    const change = postAmount - preAmount;

    if (Math.abs(change) > 0) {
      const owner = post.owner || '';
      const key = `${post.mint}:${owner}`;
      const existing = changes.get(key);
      if (existing) {
        existing.change += change;
      } else {
        changes.set(key, { mint: post.mint, owner, change });
      }
    }
  }

  // Check for accounts that existed pre-tx but were closed
  for (const pre of preTokenBalances) {
    if (pre.mint === WSOL_MINT) continue;

    const postEntry = postTokenBalances.find(
      (post) => post.accountIndex === pre.accountIndex,
    );

    if (!postEntry) {
      const preAmount = parseFloat(pre.uiTokenAmount?.uiAmountString || '0');
      if (preAmount > 0) {
        const owner = pre.owner || '';
        const key = `${pre.mint}:${owner}`;
        const existing = changes.get(key);
        if (existing) {
          existing.change -= preAmount;
        } else {
          changes.set(key, { mint: pre.mint, owner, change: -preAmount });
        }
      }
    }
  }

  return changes;
}
